// packages/core/src/components/schedule-v2/usePaintGesture.ts
//
// Le geste de peinture de l'éditeur d'horaires V2 (D1, D12, D13, D14, D25,
// D34g). UN seul hook pour souris et tactile, parce que la règle centrale
// (D1 : « le geste décide ») est la même des deux côtés — seule la manière
// de démarrer un glisser diffère :
//   - souris/stylet : un clic (pointerdown puis pointerup sans bouger) crée
//     une plage d'un pas ; un glisser peint une plage bornée (D12) ; un
//     glisser en diagonale sur plusieurs colonnes peint la même plage sur
//     chaque jour traversé, SANS bornage (D13) ; un glisser depuis le corps
//     d'une plage existante la déplace en bloc (D14, souris seulement) ; un
//     glisser depuis son bord la redimensionne (comportement D15 existant).
//   - tactile (D25) : un tap crée une plage d'un pas ET L'ARME (« extension
//     armée ») ; un second tap dans la MÊME colonne sur une cellule vide
//     étend la plage armée jusque-là ; un tap dans une autre colonne
//     désarme et recommence ailleurs ; un tap sur une plage la sélectionne
//     (désarme le mode peinture) ; un appui long (350 ms, tolérance 10px
//     D34g) puis un déplacement peint comme à la souris — jamais de
//     multi-colonnes ni de déplacement en bloc sur tactile (D13/D14).
//
// `setPointerCapture` garde tous les événements sur l'élément qui a reçu le
// pointerdown même si le doigt/la souris sort de la colonne — indispensable
// pour un glisser qui déborde de la grille visible. Pour savoir SUR QUELLE
// colonne le pointeur se trouve réellement pendant un glisser en diagonale
// (D13), on utilise `document.elementFromPoint` : la capture ne change que
// le routage des événements, pas les coordonnées écran qu'ils rapportent.
//
// Ce hook ne connaît PAS le rendu : il calcule les minutes et appelle
// `onRangesChange` avec le nouveau tableau de plages du jour concerné (déjà
// bornées/validées via schedule/intervals), ou `onSelect` pour une sélection.

import { useCallback, useMemo, useRef, useState } from "react";
import {
  boundBySiblings,
  extendRangeTo,
  floorToStep,
  moveRangeBy,
  overlapsAny,
  removeRangeAt,
  tryAddRange,
} from "../../schedule/intervals";
import type { Range } from "../../schedule/types";

export interface ScheduleSelection {
  date: string;
  index: number;
}

export interface GhostRange {
  date: string;
  start: number;
  end: number;
  /** Le geste en cours produirait un chevauchement/une plage trop courte — aperçu barré. */
  blocked: boolean;
  /** Fait partie d'un glisser multi-colonnes (D13) — un `blocked` ici se rend hachuré, pas juste barré. */
  multi?: boolean;
  /**
   * Le geste mono-colonne (D12) s'arrête au premier obstacle rencontré au
   * lieu d'aller jusqu'au point de relâchement voulu — comportement
   * volontaire (D1 : un seul geste = une seule plage, jamais de fusion
   * implicite), mais qui était silencieux : rien ne distinguait "j'ai peint
   * exactement ce que je voulais" de "je me suis arrêté plus tôt que prévu,
   * un créneau existant bloquait la suite". Signalé en test manuel.
   */
  truncated?: boolean;
  /** Le bord qui s'est arrêté tôt (jamais l'ancre, toujours fixe côté mono-colonne). */
  truncatedEdge?: "start" | "end";
}

export interface MultiColumnPaintResult {
  range: Range;
  painted: string[];
  skipped: string[];
}

export interface UsePaintGestureOptions {
  /** Pas courant en minutes — longueur d'une plage créée par un simple clic/tap. */
  stepMinutes: number;
  pxPerMin: number;
  /** Minute (depuis minuit) au sommet de la zone de dessin (offset du scroll, pas de la fenêtre visible). */
  contentTopMinute: number;
  /** Dates affichées, dans l'ordre des colonnes — pour détecter les colonnes traversées (D13). */
  dates: string[];
  getRanges: (date: string) => Range[];
  onRangesChange: (date: string, ranges: Range[]) => void;
  onSelect: (selection: ScheduleSelection | null) => void;
  /** Un glisser en diagonale a peint (ou sauté) sur plusieurs colonnes (D13). */
  onMultiColumnPaint?: (result: MultiColumnPaintResult) => void;
  selected: ScheduleSelection | null;
}

const LONG_PRESS_MS = 350;
const LONG_PRESS_TOLERANCE_PX = 10;
const CLICK_DRAG_THRESHOLD_PX = 4;
/** Largeur de la zone de bord d'une plage qui redimensionne au lieu de déplacer (D14). */
const RESIZE_EDGE_PX = 7;

function minuteFromClientY(
  clientY: number,
  columnTop: number,
  contentTopMinute: number,
  pxPerMin: number,
): number {
  return contentTopMinute + (clientY - columnTop) / pxPerMin;
}

/** Remonte jusqu'à la colonne de jour englobante (marquée `data-day-column`), même si le pointeur a démarré sur une plage à l'intérieur. */
function closestDayColumn(el: Element | null): HTMLElement | null {
  const found = el?.closest("[data-day-column]");
  return found instanceof HTMLElement ? found : null;
}

/** Quelle colonne (date) le pointeur survole-t-il réellement à l'écran, indépendamment de l'élément qui a capturé le pointeur (D13). */
function dateAtPoint(clientX: number, clientY: number): string | null {
  if (typeof document === "undefined" || !document.elementFromPoint) return null;
  const el = document.elementFromPoint(clientX, clientY);
  const col = closestDayColumn(el);
  return col?.getAttribute("data-date") ?? null;
}

type DragMode = "paint" | "resize" | "move";

interface DragState {
  pointerId: number;
  date: string;
  anchorMinute: number;
  /** Index de la plage visée si le geste a démarré SUR une plage (sélection/redimensionnement/déplacement), sinon null (peinture). */
  targetRangeIndex: number | null;
  mode: DragMode;
  moved: boolean;
  /** Tactile uniquement : le geste ne peint qu'après l'appui long. */
  armedForPaint: boolean;
  longPressTimer: ReturnType<typeof setTimeout> | null;
  startClientY: number;
  startClientX: number;
  isTouch: boolean;
}

export function usePaintGesture(options: UsePaintGestureOptions) {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [ghosts, setGhosts] = useState<GhostRange[]>([]);
  const [armed, setArmed] = useState<ScheduleSelection | null>(null);
  const dragRef = useRef<DragState | null>(null);

  const clearLongPressTimer = useCallback(() => {
    if (dragRef.current?.longPressTimer) {
      clearTimeout(dragRef.current.longPressTimer);
      dragRef.current.longPressTimer = null;
    }
  }, []);

  const endGesture = useCallback(() => {
    clearLongPressTimer();
    dragRef.current = null;
    setGhosts([]);
  }, [clearLongPressTimer]);

  /** pointerdown sur une colonne de jour (cellule vide) ou sur une plage existante. */
  const handlePointerDown = useCallback(
    (
      e: React.PointerEvent<HTMLElement>,
      date: string,
      rangeIndexUnderPointer: number | null,
    ) => {
      const isTouch = e.pointerType === "touch";
      const columnTop = (closestDayColumn(e.currentTarget) ?? e.currentTarget).getBoundingClientRect().top;
      const { contentTopMinute, pxPerMin, getRanges, stepMinutes } = optionsRef.current;
      const rawMinute = minuteFromClientY(e.clientY, columnTop, contentTopMinute, pxPerMin);
      const anchorMinute = floorToStep(rawMinute, stepMinutes);

      e.currentTarget.setPointerCapture(e.pointerId);

      // Sur une plage existante à la souris : bord (redimensionner, D15) vs
      // corps (déplacer en bloc, D14). Sur tactile, `armedForPaint` reste
      // faux dans ce cas (aucun minuteur d'appui long n'est armé ci-dessous)
      // donc `mode` n'a aucun effet — le geste s'annule s'il y a un vrai
      // déplacement, comme avant.
      let mode: DragMode = "paint";
      if (rangeIndexUnderPointer !== null) {
        mode = "resize";
        if (!isTouch) {
          const range = getRanges(date)[rangeIndexUnderPointer];
          if (range) {
            const edgeMinutes = RESIZE_EDGE_PX / pxPerMin;
            const nearStart = rawMinute - range.start <= edgeMinutes;
            const nearEnd = range.end - rawMinute <= edgeMinutes;
            mode = nearStart || nearEnd ? "resize" : "move";
          }
        }
      }

      const state: DragState = {
        pointerId: e.pointerId,
        date,
        anchorMinute,
        targetRangeIndex: rangeIndexUnderPointer,
        mode,
        moved: false,
        armedForPaint: !isTouch, // souris : peint dès le départ ; tactile : attend l'appui long
        longPressTimer: null,
        startClientY: e.clientY,
        startClientX: e.clientX,
        isTouch,
      };
      dragRef.current = state;

      if (isTouch && rangeIndexUnderPointer === null) {
        // Tactile sur cellule vide : arme le geste de peinture après
        // LONG_PRESS_MS si le doigt n'a pas bougé (D25 : "appui long puis
        // glisser"). Un tap simple (relâché avant le délai) créera une
        // plage d'un pas dans handlePointerUp, sans jamais passer par ici.
        state.longPressTimer = setTimeout(() => {
          if (dragRef.current === state) {
            state.armedForPaint = true;
            setGhosts([
              {
                date,
                start: anchorMinute,
                end: anchorMinute + optionsRef.current.stepMinutes,
                blocked: false,
              },
            ]);
          }
        }, LONG_PRESS_MS);
      }
    },
    [],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const state = dragRef.current;
    if (!state || state.pointerId !== e.pointerId) return;

    const dx = e.clientX - state.startClientX;
    const dy = e.clientY - state.startClientY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (!state.armedForPaint) {
      // Tactile, appui long pas encore écoulé : un déplacement franc annule
      // l'appui long (l'utilisateur voulait faire défiler la page, pas
      // peindre) — cf. D34g, tolérance 10px.
      if (distance > LONG_PRESS_TOLERANCE_PX) {
        clearLongPressTimer();
        dragRef.current = null;
        setGhosts([]);
      }
      return;
    }
    if (!state.moved && distance < CLICK_DRAG_THRESHOLD_PX) return; // pas encore un vrai glisser
    state.moved = true;

    const columnTop = (closestDayColumn(e.currentTarget) ?? e.currentTarget).getBoundingClientRect().top;
    const { contentTopMinute, pxPerMin, getRanges, stepMinutes, dates } = optionsRef.current;
    const towardMinute = minuteFromClientY(e.clientY, columnTop, contentTopMinute, pxPerMin);
    const existing = getRanges(state.date);

    if (state.mode === "move" && state.targetRangeIndex !== null) {
      // D14 : déplacer en bloc, accroché au pas courant, refus net (pas de
      // troncature) si ça chevaucherait une voisine.
      const deltaRaw = towardMinute - state.anchorMinute;
      const deltaSnapped = Math.round(deltaRaw / stepMinutes) * stepMinutes;
      const target = existing[state.targetRangeIndex];
      const result = moveRangeBy(existing, state.targetRangeIndex, deltaSnapped);
      setGhosts([
        {
          date: state.date,
          start: result.ok ? result.range.start : (target ? target.start + deltaSnapped : state.anchorMinute),
          end: result.ok ? result.range.end : (target ? target.end + deltaSnapped : towardMinute),
          blocked: !result.ok,
        },
      ]);
      return;
    }

    if (state.targetRangeIndex !== null) {
      // Glisser depuis le bord d'une plage existante = redimensionner (D15).
      const result = extendRangeTo(
        existing,
        state.targetRangeIndex,
        floorToStep(towardMinute, stepMinutes),
        stepMinutes,
      );
      setGhosts([
        {
          date: state.date,
          start: result.ok ? result.range.start : state.anchorMinute,
          end: result.ok ? result.range.end : towardMinute,
          blocked: !result.ok,
        },
      ]);
      return;
    }

    // Peinture (targetRangeIndex === null) : détecter un glisser en
    // diagonale sur plusieurs colonnes (D13, souris seulement).
    const currentDate = !state.isTouch ? dateAtPoint(e.clientX, e.clientY) ?? state.date : state.date;
    const anchorIndex = dates.indexOf(state.date);
    const currentIndex = dates.indexOf(currentDate);

    if (state.isTouch || anchorIndex === -1 || currentIndex === -1 || anchorIndex === currentIndex) {
      // Mono-colonne : bornage par les voisines (D12), comme avant.
      const result = boundBySiblings(state.anchorMinute, towardMinute, existing, undefined, stepMinutes);
      // Design A (retour visuel du geste tronqué) : comparer au résultat
      // SANS obstacle pour savoir si un créneau existant a coupé le geste
      // avant le point de relâchement voulu — jamais en modifiant le calcul
      // borné lui-même, seulement pour distinguer "voulu" de "tronqué".
      const unobstructed = boundBySiblings(state.anchorMinute, towardMinute, [], undefined, stepMinutes);
      const truncated =
        result.ok &&
        unobstructed.ok &&
        (result.range.start !== unobstructed.range.start || result.range.end !== unobstructed.range.end);
      // L'ancre est toujours fixe côté mono-colonne : le bord qui peut
      // s'arrêter tôt est celui vers lequel le geste grandit.
      const truncatedEdge: "start" | "end" | undefined = truncated
        ? towardMinute >= state.anchorMinute
          ? "end"
          : "start"
        : undefined;
      setGhosts([
        {
          date: state.date,
          start: result.ok ? result.range.start : Math.min(state.anchorMinute, towardMinute),
          end: result.ok ? result.range.end : Math.max(state.anchorMinute, towardMinute),
          blocked: !result.ok,
          truncated,
          truncatedEdge,
        },
      ]);
      return;
    }

    // Multi-colonnes (D13) : pas de bornage — la même plage (issue du seul
    // mouvement vertical) est prévisualisée sur chaque colonne traversée ;
    // une colonne où elle chevaucherait une plage existante est marquée
    // bloquée (rendue hachurée par l'appelant) plutôt que tronquée.
    const lo = Math.min(anchorIndex, currentIndex);
    const hi = Math.max(anchorIndex, currentIndex);
    const traversed = dates.slice(lo, hi + 1);
    const towardSnapped = floorToStep(towardMinute, stepMinutes);
    const rawStart = Math.min(state.anchorMinute, towardSnapped);
    const rawEnd = Math.max(state.anchorMinute, towardSnapped);
    if (rawEnd <= rawStart) {
      setGhosts([]);
      return;
    }
    const candidate: Range = { start: rawStart, end: rawEnd };
    setGhosts(
      traversed.map((d) => ({
        date: d,
        start: candidate.start,
        end: candidate.end,
        blocked: overlapsAny(candidate, getRanges(d)),
        multi: true,
      })),
    );
  }, [clearLongPressTimer]);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const state = dragRef.current;
      if (!state || state.pointerId !== e.pointerId) {
        endGesture();
        return;
      }
      const { getRanges, onRangesChange, onSelect, onMultiColumnPaint, stepMinutes, selected, dates } =
        optionsRef.current;
      const existing = getRanges(state.date);

      if (!state.moved) {
        clearLongPressTimer();
        // Pas de déplacement significatif : clic/tap simple.
        if (state.targetRangeIndex !== null) {
          // Clic sur une plage existante = sélection (D11) ; tactile idem (D25).
          onSelect({ date: state.date, index: state.targetRangeIndex });
          setArmed(null);
          endGesture();
          return;
        }
        if (e.pointerType === "touch") {
          // D25 : tap sur cellule vide.
          if (armed && armed.date === state.date) {
            // Second tap dans LA MÊME colonne sur une cellule vide → étend
            // la plage armée jusqu'ici.
            const result = extendRangeTo(existing, armed.index, state.anchorMinute, stepMinutes);
            if (result.ok) {
              const next = existing.slice();
              next[armed.index] = result.range;
              onRangesChange(state.date, next);
              setArmed({ date: state.date, index: armed.index });
            }
            endGesture();
            return;
          }
          // Tap dans une autre colonne (ou rien d'armé) : nouvelle plage
          // d'un pas, armée.
          const created = boundBySiblings(
            state.anchorMinute,
            state.anchorMinute + stepMinutes,
            existing,
            undefined,
            stepMinutes,
          );
          if (created.ok) {
            const next = [...existing, created.range];
            onRangesChange(state.date, next);
            setArmed({ date: state.date, index: next.length - 1 });
            onSelect({ date: state.date, index: next.length - 1 });
          }
          endGesture();
          return;
        }

        // Souris : Maj + clic étend la sélection courante (D15).
        if (e.shiftKey && selected && selected.date === state.date) {
          const result = extendRangeTo(
            getRanges(selected.date),
            selected.index,
            state.anchorMinute,
            stepMinutes,
          );
          if (result.ok) {
            const next = getRanges(selected.date).slice();
            next[selected.index] = result.range;
            onRangesChange(selected.date, next);
          }
          endGesture();
          return;
        }

        // Clic simple sur cellule vide = plage d'un pas (D1 : le geste
        // décide ; un clic est un glisser de distance nulle).
        const created = boundBySiblings(
          state.anchorMinute,
          state.anchorMinute + stepMinutes,
          existing,
          undefined,
          stepMinutes,
        );
        if (created.ok) {
          const next = [...existing, created.range];
          onRangesChange(state.date, next);
          onSelect({ date: state.date, index: next.length - 1 });
        }
        endGesture();
        return;
      }

      // Un vrai glisser a eu lieu.
      setArmed(null);
      const columnTop = (closestDayColumn(e.currentTarget) ?? e.currentTarget).getBoundingClientRect().top;
      const { contentTopMinute, pxPerMin } = optionsRef.current;
      const towardMinute = minuteFromClientY(e.clientY, columnTop, contentTopMinute, pxPerMin);

      if (state.mode === "move" && state.targetRangeIndex !== null) {
        // D14 : commit du déplacement, ou rien si le résultat est refusé
        // (chevauchement/hors bornes) — la plage reste où elle était.
        const deltaRaw = towardMinute - state.anchorMinute;
        const deltaSnapped = Math.round(deltaRaw / stepMinutes) * stepMinutes;
        const result = moveRangeBy(existing, state.targetRangeIndex, deltaSnapped);
        if (result.ok) {
          const next = existing.slice();
          next[state.targetRangeIndex] = result.range;
          onRangesChange(state.date, next);
          onSelect({ date: state.date, index: state.targetRangeIndex });
        }
        endGesture();
        return;
      }

      if (state.targetRangeIndex !== null) {
        const result = extendRangeTo(
          existing,
          state.targetRangeIndex,
          floorToStep(towardMinute, stepMinutes),
          stepMinutes,
        );
        if (result.ok) {
          const next = existing.slice();
          next[state.targetRangeIndex] = result.range;
          onRangesChange(state.date, next);
        }
        endGesture();
        return;
      }

      // Peinture : même détection multi-colonnes qu'au déplacement (D13).
      const currentDate = !state.isTouch ? dateAtPoint(e.clientX, e.clientY) ?? state.date : state.date;
      const anchorIndex = dates.indexOf(state.date);
      const currentIndex = dates.indexOf(currentDate);

      if (!state.isTouch && anchorIndex !== -1 && currentIndex !== -1 && anchorIndex !== currentIndex) {
        const lo = Math.min(anchorIndex, currentIndex);
        const hi = Math.max(anchorIndex, currentIndex);
        const traversed = dates.slice(lo, hi + 1);
        const towardSnapped = floorToStep(towardMinute, stepMinutes);
        const rawStart = Math.min(state.anchorMinute, towardSnapped);
        const rawEnd = Math.max(state.anchorMinute, towardSnapped);
        if (rawEnd > rawStart) {
          const candidate: Range = { start: rawStart, end: rawEnd };
          const painted: string[] = [];
          const skipped: string[] = [];
          let anchorNewIndex: number | null = null;
          for (const d of traversed) {
            const dayExisting = getRanges(d);
            const result = tryAddRange(dayExisting, candidate);
            if (result.ok) {
              onRangesChange(d, [...dayExisting, result.range]);
              painted.push(d);
              if (d === state.date) anchorNewIndex = dayExisting.length;
            } else {
              skipped.push(d);
            }
          }
          onSelect(anchorNewIndex !== null ? { date: state.date, index: anchorNewIndex } : null);
          onMultiColumnPaint?.({ range: candidate, painted, skipped });
        }
        endGesture();
        return;
      }

      const result = boundBySiblings(state.anchorMinute, towardMinute, existing, undefined, stepMinutes);
      if (result.ok && !overlapsAny(result.range, existing)) {
        const next = [...existing, result.range];
        onRangesChange(state.date, next);
        onSelect({ date: state.date, index: next.length - 1 });
      }
      endGesture();
    },
    [armed, clearLongPressTimer, endGesture],
  );

  const handlePointerCancel = useCallback(() => {
    setArmed(null);
    endGesture();
  }, [endGesture]);

  /** Toute autre action désarme l'extension mobile (D25 : "toute autre action désarme"). */
  const disarm = useCallback(() => setArmed(null), []);

  const deleteSelected = useCallback(
    (selection: ScheduleSelection) => {
      const existing = optionsRef.current.getRanges(selection.date);
      optionsRef.current.onRangesChange(
        selection.date,
        removeRangeAt(existing, selection.index),
      );
      if (armed && armed.date === selection.date && armed.index === selection.index) {
        setArmed(null);
      }
    },
    [armed],
  );

  return useMemo(
    () => ({
      ghosts,
      armed,
      disarm,
      deleteSelected,
      handlers: {
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
        onPointerCancel: handlePointerCancel,
      },
    }),
    [ghosts, armed, disarm, deleteSelected, handlePointerDown, handlePointerMove, handlePointerUp, handlePointerCancel],
  );
}
